/**
 * API Configuration and Service Layer
 * Handles dynamic API base URL configuration and provides a centralized API client
 */

import { handleApiError } from "./toastUtils";
import { appConfig } from "../config/appConfig";
import { useApiStore } from "../stores/apiStore";
import { useUserStore } from "../stores/userStore";

// Default/Fallback Configuration (when public/config.json is missing or invalid)
const DEFAULT_RUNTIME_CONFIG = {
  project: "orderingv1",
  api: {
    url: "https://apiv4.ordering.co",
    version: "v400",
    language: "en",
  },
};

// Simple function to build headers only with existing values
const buildHeaders = () => {
  const headers = {
    "Content-Type": "application/json",
  };

  // Only add headers if their values exist
  if (import.meta.env.VITE_X_API_KEY) {
    headers["X-Api-Key"] = import.meta.env.VITE_X_API_KEY;
  }

  // X-APP-X comes from appConfig.app_name
  if (appConfig?.app_name) {
    headers["X-APP-X"] = appConfig.app_name;
  }

  // X-INTERNAL-PRODUCT-X comes from appConfig.app_internal_name
  if (appConfig?.app_internal_name) {
    headers["X-INTERNAL-PRODUCT-X"] = appConfig.app_internal_name;
  }
  if (import.meta.env.VITE_X_COUNTRY_CODE_X) {
    headers["X-Country-Code-X"] = import.meta.env.VITE_X_COUNTRY_CODE_X;
  }

  return headers;
};

// API base configuration (static defaults)
const API_CONFIG = {
  timeout: parseInt(import.meta.env.VITE_API_TIMEOUT) || 20000,
  headers: buildHeaders(),
};

/**
 * ConfigManager
 * Uses static import of config.json for better performance.
 * Now supports dynamic project configuration from API store.
 */
class ConfigManager {
  static instance;

  static getInstance() {
    if (!ConfigManager.instance) {
      ConfigManager.instance = new ConfigManager();
    }
    return ConfigManager.instance;
  }

  constructor() {
    // Use imported config data directly
    this.config = appConfig || DEFAULT_RUNTIME_CONFIG;
  }

  getConfig() {
    return this.config;
  }

  getBaseUrl() {
    // Try to get project from API store first (dynamic)
    const apiStore = useApiStore.getState();
    const dynamicProject = apiStore.project;
    const dynamicLanguage = apiStore?.api?.language;

    // Fallback to static config
    const cfg = this.getConfig();
    const api = cfg?.api || {};
    const url = api.url || DEFAULT_RUNTIME_CONFIG.api.url;
    const version = api.version || DEFAULT_RUNTIME_CONFIG.api.version;
    const language =
      dynamicLanguage || api.language || DEFAULT_RUNTIME_CONFIG.api.language;

    // Use dynamic project if available, otherwise fallback to config
    const project =
      dynamicProject || cfg?.project || DEFAULT_RUNTIME_CONFIG.project;

    return `${url.replace(/\/$/, "")}/${version}/${language}/${project}`;
  }
}

/**
 * Custom API Error class for better error handling
 */
class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

/**
 * API Client class for making HTTP requests
 */
class ApiClient {
  constructor(config = {}) {
    this.timeout = config.timeout || API_CONFIG.timeout;
    this.defaultHeaders = { ...buildHeaders(), ...config.headers };
    this.configManager = config.configManager || ConfigManager.getInstance();
    this.toast = config.toast || null; // Toast instance for error handling
    this.dynamicHeaders = {}; // Store for dynamic headers
    this.isLoggingOut = false; // Flag to prevent multiple logout attempts
  }

  /**
   * Get the full URL for an endpoint using static config
   */
  getUrl(endpoint, baseUrlOverride) {
    const baseUrl = baseUrlOverride || this.configManager.getBaseUrl();
    return `${baseUrl.replace(/\/$/, "")}${
      endpoint.startsWith("/") ? endpoint : `/${endpoint}`
    }`;
  }

  /**
   * Make an HTTP request
   */
  async request(endpoint, options = {}) {
    let url = this.getUrl(endpoint, options.url);

    // Handle query parameters for GET requests
    if (options.params && (options.method === "GET" || !options.method)) {
      const searchParams = new URLSearchParams();
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, value);
        }
      });
      if (searchParams.toString()) {
        url += `?${searchParams.toString()}`;
      }
    }

    // Build headers with dynamic headers included
    const allHeaders = { ...this.defaultHeaders, ...this.dynamicHeaders };

    const config = {
      method: "GET",
      headers: { ...allHeaders },
      ...options,
    };

    // Add authorization header if token exists
    const token = this.getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Handle request body
    if (config.body && typeof config.body === "object") {
      config.body = JSON.stringify(config.body);
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      const response = await fetch(url, {
        ...config,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        // Auto-logout on 401 Unauthorized (invalid/expired session)
        if (response.status === 401 && !this.isLoggingOut) {
          const userStore = useUserStore.getState();

          // Only logout if user is authenticated and not already logging out
          if (userStore.isAuthenticated) {
            this.isLoggingOut = true;

            try {
              // Clear auth token immediately
              this.setAuthToken(null);

              // Clear localStorage items related to auth/session
              if (typeof window !== "undefined") {
                const keysToRemove = [];
                for (let i = 0; i < localStorage.length; i++) {
                  const key = localStorage.key(i);
                  if (
                    key &&
                    (key.includes("auth") ||
                      key.includes("session") ||
                      key.includes("user") ||
                      key.includes("business"))
                  ) {
                    keysToRemove.push(key);
                  }
                }
                keysToRemove.forEach((key) => localStorage.removeItem(key));

                // Clear business state if available
                if (window.clearBusinessState) {
                  window.clearBusinessState();
                }
              }

              // Reload page to redirect to login
              if (typeof window !== "undefined") {
                setTimeout(() => {
                  window.location.href = "/";
                }, 100);
              }
            } catch (error) {
              console.error("Auto-logout error:", error);
            } finally {
              // Reset flag after reload
              setTimeout(() => {
                this.isLoggingOut = false;
              }, 1000);
            }
          }
        }

        // Handle different error response formats
        let errorMessage = errorData.message;

        // If no message but has result field (array or string)
        if (!errorMessage && errorData.result) {
          if (Array.isArray(errorData.result)) {
            errorMessage = errorData.result.join(", ");
          } else {
            errorMessage = errorData.result;
          }
        }

        // Convert underscores to spaces for better readability
        if (errorMessage && typeof errorMessage === "string") {
          errorMessage = errorMessage.replace(/_/g, " ");
        }

        // Fallback to generic HTTP error message
        if (!errorMessage) {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }

        throw new ApiError(errorMessage, response.status, errorData);
      }

      const contentType = response.headers.get("content-type") || "";

      if (
        options.responseType === "blob" ||
        contentType.includes("application/pdf")
      ) {
        return await response.blob();
      }

      if (contentType.includes("application/json")) {
        const jsonData = await response.json();

        // Check if response contains error field (backend error format)
        if (jsonData.error) {
          let errorMessage = Array.isArray(jsonData.result)
            ? jsonData.result.join(", ")
            : jsonData.result || "Request failed";

          // Convert underscores to spaces for better readability
          if (errorMessage && typeof errorMessage === "string") {
            errorMessage = errorMessage.replace(/_/g, " ");
          }

          throw new ApiError(errorMessage, response.status, jsonData);
        }

        return jsonData;
      }
      return await response.text();
    } catch (error) {
      if (error.name === "AbortError") {
        const timeoutError = new ApiError("Request timeout", 408);
        handleApiError(timeoutError, this.toast);
        throw timeoutError;
      }

      // Handle API errors with toast
      if (error instanceof ApiError) {
        handleApiError(error, this.toast);
      }

      throw error;
    }
  }

  /**
   * Get authentication token from localStorage
   */
  getAuthToken() {
    if (typeof window !== "undefined") {
      return localStorage.getItem("auth_token");
    }
    return null;
  }

  /**
   * Set authentication token
   */
  setAuthToken(token) {
    if (typeof window !== "undefined") {
      if (token) {
        localStorage.setItem("auth_token", token);
      } else {
        localStorage.removeItem("auth_token");
      }
    }
  }

  /**
   * Set toast instance for error handling
   */
  setToast(toastInstance) {
    this.toast = toastInstance;
  }

  /**
   * Set a dynamic header that will be included in all requests
   * @param {string} name - Header name
   * @param {string} value - Header value (null/undefined to remove)
   */
  setDynamicHeader(name, value) {
    if (value === null || value === undefined) {
      delete this.dynamicHeaders[name];
    } else {
      this.dynamicHeaders[name] = value;
    }
  }

  /**
   * Get current dynamic headers
   */
  getDynamicHeaders() {
    return { ...this.dynamicHeaders };
  }

  // HTTP Methods
  async get(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: "GET" });
  }

  async post(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: "POST",
      body: data,
    });
  }

  async put(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: "PUT",
      body: data,
    });
  }

  async patch(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: "PATCH",
      body: data,
    });
  }

  async delete(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: "DELETE" });
  }
}

// Create default API client instance
const apiClient = new ApiClient();

// Export both the class and instance
export { ApiClient, ApiError };
export default apiClient;
